import { Context, ExtraContext } from '@utilitywarehouse/tracking-core';

import {
  ProductSubtype,
  ProductType,
  Quote,
  SelectBhcQuoteMutation,
} from '@generated/gql/graphql';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupCoreServicesEntity,
  useTrackSignupBhcAddedToBasketOnce,
} from '@tracking/trackingEvents';
import { getGroupCombinations } from '@tracking/utils';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';
import { parseValueToPennies } from '@utils/price';

const getBhcMonthlyCost = (quotes: Quote[]) => {
  const bhcQuote = quotes.find(
    (quote) =>
      quote.product.type === ProductType.Insurance &&
      quote.product.subtype === ProductSubtype.BoilerHomeCover &&
      quote.selected
  );

  return bhcQuote?.totalMonthlyCost?.value || '';
};

const updateTrackingContext = (
  contexts: ExtraContext<Context>[],
  selectResult: SelectBhcQuoteMutation
): ExtraContext<Context>[] => {
  const coreServiceContextIndex = contexts.findIndex(
    ({ name }) => name === 'signup_core_services'
  );

  // Should never happen
  if (coreServiceContextIndex === -1) {
    return contexts;
  }

  const coreServiceContext = contexts[coreServiceContextIndex]
    .data as SignupCoreServicesEntity;

  const {
    selectQuote: {
      monthlyCost: { value: totalPrice },
      quotes,
    },
  } = selectResult;

  if (!coreServiceContext.insurance) {
    contexts[coreServiceContextIndex].data.insurance = true;
    contexts[coreServiceContextIndex].data.number_of_core_services++;
  }

  const bhcPrice = getBhcMonthlyCost(quotes as Quote[]);

  if (bhcPrice) {
    contexts[coreServiceContextIndex].data.insurance_price =
      parseValueToPennies(bhcPrice);
  }

  contexts[coreServiceContextIndex].data.total_price =
    parseValueToPennies(totalPrice);
  contexts[
    coreServiceContextIndex
  ].data.core_service_group_quantity_combinations =
    getGroupCombinations(coreServiceContext);

  return contexts;
};

const useTrackBhcSelected = () => {
  const { trackingContexts } = useTrackingContext();
  const trackBhcAddedToBasket = useTrackSignupBhcAddedToBasketOnce();

  const trackCoverSelected = (basketData: SelectBhcQuoteMutation) => {
    const updatedTrackingContexts = updateTrackingContext(
      trackingContexts,
      basketData
    );

    trackBhcAddedToBasket(
      {},
      {
        contexts: [...updatedTrackingContexts, ...buildOptimizelyEntity()],
      }
    );
  };

  return { trackCoverSelected };
};

export { useTrackBhcSelected };
